import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Search } from 'lucide-react';

import DataTable from '../ui/DataTable';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge, { readinessVariant } from '../ui/Badge';
import ReadinessModal from '../ui/ReadinessModal';
import TeamsService from "../../services/TeamsService"
import AnnouncementService from '../../services/AnnouncementService';
import AnnouncementMemberService from '../../services/AnnouncementMemberService';
import HospitalFacilityService from '../../services/HospitalFacilityService';
import ReadinessLevelService from '../../services/ReadinessLevelService';
import { processTeamData } from '../../utils/utils';

function TeamList() {

    const navigate = useNavigate();

    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [editName, setEditName] = useState('');
    const [editHospitalId, setEditHospitalId] = useState('');
    const [editTeamId, setEditTeamId] = useState(null);

    // Activation dialog state
    const [activateOpen, setActivateOpen] = useState(false);
    const [activateType, setActivateType] = useState('announcement'); // 'announcement' | 'drill'
    const [activateItems, setActivateItems] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [activating, setActivating] = useState(false);

    // Readiness dialog state
    const [hospitals, setHospitals] = useState([]);
    const [readinessLevelIds, setReadinessLevelIds] = useState({}); // { Normal: 1, Alert: 2, Standby: 3, Active: 4 }
    const [readinessRow, setReadinessRow] = useState(null);
    const [readinessBusy, setReadinessBusy] = useState(false);

    const loadTeams = async () => {
        try {
            let response = await TeamsService.findAll(1, 100);
            if (response?.status === 200) {
                setTeams(processTeamData(response.data));
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const loadHospitals = async () => {
        try {
            const response = await HospitalFacilityService.findAll(true);
            if (response?.status === 200) setHospitals(response.data);
        } catch (error) {
            console.log(error);
        }
    };

    const loadReadinessLevels = async () => {
        try {
            const response = await ReadinessLevelService.findAll();
            if (response?.status === 200) {
                const map = {};
                response.data.forEach((rl) => { map[rl.level] = rl.id; });
                setReadinessLevelIds(map);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => { loadTeams(); loadHospitals(); loadReadinessLevels(); }, []);

    const headCells = [
        { id: 'name', label: 'Name' },
        { id: 'hospitalName', label: 'Hospital', render: (row) => row.hospitalName || '—' },
        {
            id: 'readinessLevel',
            label: 'Readiness',
            render: (row) => <Badge variant={readinessVariant(row.readinessLevel)}>{row.readinessLevel || 'Normal'}</Badge>,
        },
        { id: 'totalMembers', label: 'Total' },
        { id: 'doctors', label: 'Doctors' },
        { id: 'admins', label: 'Admins' },
        { id: 'nurses', label: 'Nurses' },
        { id: 'rsos', label: 'RSOs' },
    ];

    const handleEdit = (row) => {
        setEditTeamId(row.id);
        setEditName(row.name);
        setEditHospitalId(row.hospitalFacilityId || '');
        setEditOpen(true);
    };

    const handleEditSave = async () => {
        if (!editName.trim()) {
            toast.info("Please enter a team name");
            return;
        }
        try {
            await TeamsService.update({ id: editTeamId, name: editName.trim(), hospitalFacilityId: editHospitalId || null });
            toast.success("Team updated successfully");
            setEditOpen(false);
            loadTeams();
        } catch (error) {
            toast.error("Failed to update team");
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete team "${row.name}"?`)) return;
        try {
            await TeamsService.delete(row.id);
            toast.success("Team deleted successfully");
            loadTeams();
        } catch (error) {
            toast.error("Failed to delete team");
        }
    };

    const handleView = (row) => {
        navigate(`${row.id}`);
    };

    const openReadiness = (row) => setReadinessRow(row);
    const closeReadiness = () => setReadinessRow(null);

    const handleSetReadiness = async (level) => {
        const readinessLevelId = readinessLevelIds[level];
        if (!readinessLevelId) return;
        setReadinessBusy(true);
        try {
            await TeamsService.update({ id: readinessRow.id, readinessLevelId });
            toast.success(`Readiness set to ${level}`);
            closeReadiness();
            loadTeams();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to update readiness level");
        } finally {
            setReadinessBusy(false);
        }
    };

    const openActivateDialog = async (type, row) => {
        setActivateType(type);
        setSelectedTeamId(row.id);
        try {
            const response = await AnnouncementService.findAll(1, 100);
            if (response?.status === 200) {
                const isDrill = type === 'drill';
                setActivateItems(response.data.filter(a => a.drill === isDrill));
            }
        } catch (error) {
            toast.error("Failed to load " + type + "s");
        }
        setActivateOpen(true);
    };

    const handleActivateSelect = async (announcementId) => {
        if (activating) return;
        setActivating(true);
        try {
            const response = await AnnouncementMemberService.activateAll(announcementId, selectedTeamId);
            toast.success(response.message || "Team activated successfully");
            setActivateOpen(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Activation failed");
        } finally {
            setActivating(false);
        }
    };

    const options = [
        { title: 'Activate Announcement', handler: (row) => openActivateDialog('announcement', row) },
        { title: 'Activate Drill', handler: (row) => openActivateDialog('drill', row) },
        { title: 'Manage Readiness', handler: (row) => openReadiness(row) },
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="relative w-full sm:w-80">
                    <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500" />
                    <input
                        type="search"
                        placeholder="Search teams..."
                        className="form-input pl-9"
                    />
                </div>
                <Button icon={Plus} onClick={() => navigate("add")}>New Team</Button>
            </div>

            <DataTable
                title="EMS Teams"
                rows={teams}
                headCells={headCells}
                moreOptions={options}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                loading={loading}
                emptyLabel={loading ? 'Loading teams…' : 'No teams found.'}
            />

            {/* Edit Team Modal */}
            <Modal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title="Edit Team"
                onConfirm={handleEditSave}
                confirmLabel="Save"
            >
                <div className="space-y-4">
                    <div>
                        <label className="form-label">Team Name</label>
                        <input
                            className="form-input"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="form-label">Hospital</label>
                        <select
                            className="form-input bg-white dark:bg-ink-900"
                            value={editHospitalId}
                            onChange={(e) => setEditHospitalId(e.target.value)}
                        >
                            <option value="">No hospital assigned</option>
                            {hospitals.map((h) => (
                                <option key={h.id} value={h.id}>{h.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </Modal>

            <ReadinessModal
                open={Boolean(readinessRow)}
                subjectLabel={readinessRow ? `${readinessRow.name} — ${readinessRow.hospitalName || 'No hospital'}` : ''}
                currentLevel={readinessRow?.readinessLevel}
                onClose={closeReadiness}
                onSelect={handleSetReadiness}
                busy={readinessBusy}
            />

            {/* Activate Announcement/Drill Modal */}
            <Modal
                open={activateOpen}
                onClose={() => setActivateOpen(false)}
                title={`Select ${activateType === 'drill' ? 'Drill' : 'Announcement'} to Activate`}
                footer={null}
            >
                <div className="space-y-2 max-h-96 overflow-y-auto">
                    {activateItems.length === 0 && (
                        <p className="text-sm text-neutral-400 text-center py-6 dark:text-neutral-500">No {activateType}s available</p>
                    )}
                    {activateItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => handleActivateSelect(item.id)}
                            disabled={activating}
                            className="w-full text-left px-4 py-3 rounded-lg border border-neutral-200 hover:border-seha-300 hover:bg-seha-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-neutral-200 disabled:hover:bg-transparent dark:border-neutral-700 dark:hover:bg-seha-500/10"
                        >
                            <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100">{item.title}</p>
                            <p className="text-xs text-neutral-500 mt-0.5 dark:text-neutral-500">
                                {activating ? 'Activating…' : (item.location || 'No location')} &mdash; {item.incidentTypeNames?.join(' / ') || 'N/A'}
                            </p>
                        </button>
                    ))}
                </div>
            </Modal>
        </div>
    )
}

export default TeamList
